/** @jsx jsx */

import { jsx, keyframes } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors } from '../../../../theme';
import { type StrictExtract } from '@/types';
import { ColorName } from '@/theme';

const streakAnimation = keyframes({
  '0%': {
    transform: 'translateX(-100%)',
  },
  '100%': {
    transform: 'translateX(100%)',
  },
});

type Neutrals = StrictExtract<
  ColorName,
  | 'GrayWarm1'
  | 'GrayWarm2'
  | 'GrayWarm3'
  | 'GrayWarm4'
  | 'GrayWarm5'
  | 'GrayWarm6'
  | 'GrayWarm7'
  | 'GrayWarm8'
  | 'GrayWarm9'
  | 'GrayWarm10'
>;

const Styles = labelStyles('SkeletonBlock', {
  root: (color: Neutrals) => [
    {
      backgroundColor: Colors[color],
      overflow: 'hidden',
      position: 'relative',
      borderRadius: 'var(--zds-radius-medium)',

      '&::before': {
        animation: `${streakAnimation} 1s ease-in-out infinite`,
        content: '""',
        background: `linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0))`,
        position: 'absolute',
        top: '0',
        right: '0',
        bottom: '0',
        left: '0',
      },

      '&[data-border-radius="none"]': {
        borderRadius: 'var(--zds-radius-none)',
      },
      '&[data-border-radius="small"]': {
        borderRadius: 'var(--zds-radius-small)',
      },
      '&[data-border-radius="large"]': {
        borderRadius: 'var(--zds-radius-large)',
      },
    },
  ],
});

type Props = {
  /**
   * An aspect ratio to maintain for the element.
   * **Important:** This takes precedence over `height`.
   */
  aspectRatio?: number;
  /**
   * Border radius of the block.
   */
  borderRadius?: 'none' | 'small' | 'medium' | 'large';
  /**
   * Any valid CSS `height` value.
   */
  height?: number | string;
  /**
   * Any valid CSS `width` value.
   */
  width?: number | string;
  /**
   * The color of the block.
   */
  color?: StrictExtract<
    ColorName,
    | 'GrayWarm1'
    | 'GrayWarm2'
    | 'GrayWarm3'
    | 'GrayWarm4'
    | 'GrayWarm5'
    | 'GrayWarm6'
    | 'GrayWarm7'
    | 'GrayWarm8'
    | 'GrayWarm9'
    | 'GrayWarm10'
  >;
};

/**
 * `SkeletonBlock` renders an abstract representation of content, typically as content loads. It exposes a minimal interface that other components can leverage.
 */
export const SkeletonBlock = ({
  aspectRatio = undefined,
  borderRadius = 'medium',
  height = undefined,
  width = '100%',
  color = 'GrayWarm2',
}: Props) => {
  const style = {
    width: width,
    paddingTop: aspectRatio ? `calc(${width} / ${aspectRatio})` : height,
  };
  return (
    <div
      css={Styles.root(color)}
      data-border-radius={borderRadius}
      data-testid="SkeletonBlock"
      data-zds
      style={style}
    />
  );
};
